import type { InternalDatapointOptions, UnifiedEvent } from 'owa-analytics-types';
import { lazyLogUsage } from '../lazyFunctions';
import { addStackTraceToEvent } from 'owa-analytics-utils/lib/addStackTraceToEvent';
import { yieldNow } from 'owa-task-queue/lib/schedule';

/**
 * Logs *only* to Unified Telemetry (bypassing client_event, etc).
 *
 * Internally re-uses the same lazy pipeline as logUsage,
 * but passes unifiedOnly so logDatapoint will skip everything but UT.
 *
 * @param unifiedEvent  A strongly-typed UT event (includes eventName and data).
 * @param shouldYield   If true, yields before logging to avoid blocking operations.
 */
export async function logUnifiedTelemetry(
    unifiedEvent: UnifiedEvent,
    shouldYield?: boolean
): Promise<void> {
    if (shouldYield) {
        yieldNow('logUnifiedTelemetry');
    }

    const { eventName } = unifiedEvent;

    // Indicate that this unified telemetry event does not need to be matched
    // against a non-UT event since no pairing is possible for UT only events.
    unifiedEvent.bypassEventMatchingCheck = true;

    const datapointOptions = {
        unifiedTelemetry: unifiedEvent,
        unifiedTelemetryOnly: true,
    } as InternalDatapointOptions;

    addStackTraceToEvent(datapointOptions);

    return lazyLogUsage.importAndExecute(
        eventName as Parameters<typeof lazyLogUsage.importAndExecute>[0],
        undefined /* non-UT CustomData */,
        datapointOptions
    );
}
